'use client';

import type { MutableRefObject } from 'react';
import { useT } from '@/components/i18n-provider';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Shared panel chrome styles so every view's action buttons, segments, and
 * filter chips look identical (rail-redesign consistency pass). Use these
 * instead of hand-rolling per-view classes.
 */

/** Borderless icon action button in a panel header (search, settings, share,
 *  create, close) — a plain icon with a hover wash, matching the wireframes. */
export const PANEL_ICON_BTN =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground';

/** A labelled action button (e.g. "New") in a panel header. */
export const PANEL_BTN =
  'inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border border-border/60 px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent';

/** The header "+ Create" badge — quiet outline pill (Option C: all-outline
 *  chrome so the header never stacks competing filled elements; underline =
 *  nav, border = input, outline = actions & meta). */
export const PANEL_CREATE_BADGE =
  'inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-border px-2.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent';

/**
 * Ref callback that puts the cursor in a panel view's primary search /
 * filter input the moment the view mounts — so opening Apps / Agents /
 * Content / Spaces (rail click or ⌘⇧ chord) lets the user type
 * immediately. Desktop pointers only: autofocus pops the soft keyboard
 * on touch. The dataset flag keeps later render commits (ref callbacks
 * re-fire on every commit) from re-stealing focus mid-session.
 */
export function panelAutofocusRef(el: HTMLInputElement | null): void {
  if (!el || el.dataset.autofocused) return;
  if (typeof window === 'undefined') return;
  if (!window.matchMedia('(pointer: fine)').matches) return;
  el.dataset.autofocused = '1';
  el.focus();
}

/**
 * THE panel search input. Every left-panel view (Search, Spaces, Apps,
 * Agents, Content, chats/runs lists, …) renders this one component so
 * the field reads identically everywhere: same height, same type
 * size, same icon, same clear affordance. Callers keep their own
 * outer layout (toolbars, sort menus, chips) and drop this in as the
 * flexible middle.
 *
 * - Autofocuses on mount by default (see `panelAutofocusRef`) — the
 *   "open panel → type immediately" contract.
 * - `hint` renders a keyboard chip (e.g. "⌘⇧K") that yields to the
 *   clear button once the user has typed.
 * - `inputRef` is for callers that refocus programmatically (the
 *   Search view's ⌘K handler); merged with the autofocus ref.
 */
export function PanelSearchInput({
  value,
  onChange,
  placeholder,
  hint,
  autoFocus = true,
  inputRef,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  /** Keyboard-shortcut chip shown inside the field while it's empty. */
  hint?: string;
  /** Put the cursor here when the view mounts. Default true. */
  autoFocus?: boolean;
  /** Extra ref for programmatic refocus. */
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  /** Layout classes for the wrapper (e.g. `flex-1`). */
  className?: string;
}) {
  const t = useT();
  return (
    <div className={cn('relative min-w-0', className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        ref={(el) => {
          if (inputRef) inputRef.current = el;
          if (autoFocus) panelAutofocusRef(el);
        }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(
          // Soft fill — the input is the ONE filled element of the panel
          // chrome (tabs are underline, buttons and chips are outline).
          'ws-selectable h-9 w-full rounded-lg pl-8 text-sm',
          // The field draws its own clear button (below). `type="search"`
          // also gets the browser's, so a typed query showed two (portal
          // visual pass, 2026-09-30).
          '[&::-webkit-search-cancel-button]:hidden',
          value || hint ? 'pr-14' : 'pr-2',
          'bg-foreground/5 dark:bg-foreground/8',
          'focus:bg-foreground/8 focus:outline-none dark:focus:bg-foreground/12',
          'placeholder:text-muted-foreground transition-colors',
        )}
      />
      {value ? (
        <button
          type="button"
          aria-label={t('workspace.panels.clearSearch')}
          onClick={() => onChange('')}
          className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : hint ? (
        <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 items-center rounded-md border border-border/60 bg-muted/50 px-1.5 py-1 font-mono text-xs leading-none text-muted-foreground sm:inline-flex">
          {hint}
        </kbd>
      ) : null}
    </div>
  );
}

/** Row container for panel tabs — a full-width hairline the active tab's
 *  underline sits on. Use with `panelSegmentBtn` children. */
export const PANEL_TABS_ROW = 'flex w-full items-end gap-1 border-b border-border/60';

/** Panel tab (e.g. Explore · Yours, Activity · Channels) — compact,
 *  left-aligned underline tabs on the row's hairline (never a gray fill),
 *  converging with the standalone marketplace page's tabs. */
export function panelSegmentBtn(active: boolean): string {
  return cn(
    'shrink-0 -mb-px border-b-2 px-3 pb-2 pt-1 text-xs font-medium capitalize transition-colors',
    active
      ? 'border-foreground text-foreground'
      : 'border-transparent text-muted-foreground hover:text-foreground',
  );
}

/** Filter / scope chip (e.g. Trending · Design, or This workspace · Everywhere). */
export function panelChip(active: boolean): string {
  return cn(
    'shrink-0 rounded-full px-2.5 py-1 text-[11px] transition-colors',
    active
      ? 'bg-foreground text-background'
      : 'border border-border/60 text-muted-foreground hover:bg-accent/50 hover:text-foreground',
  );
}
